import React, { useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import { Close } from "mixins/svgIcons";

type EmbedModalProps = {
  isOpen: boolean;
  onClose: () => void;
  embedCode: string | null;
};

const EmbedModal: React.FC<EmbedModalProps> = ({ isOpen, onClose, embedCode }) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscKey);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [isOpen, onClose]);

  const sanitizedEmbedCode = DOMPurify.sanitize(String(embedCode), {
    ADD_TAGS: ["iframe", "div"],
    ADD_ATTR: [
      "allow",
      "allowfullscreen",
      "frameborder",
      "scrolling",
      "webkitallowfullscreen",
      "mozallowfullscreen",
      "allowtransparency",
      "style",
      "src"
    ]
  });

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (modalRef.current && e.target === modalRef.current) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={modalRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 transition-opacity duration-300 p-4"
      onClick={handleBackdropClick}
      role="presentation"
      data-testid="embed-modal-backdrop"
    >
      <div className="relative w-full md:mx-auto md:w-4/5 md:max-w-1140 overflow-hidden bg-opacity-75">
        <div className="w-full flex justify-end py-2">
          <button
            className="flex items-center justify-center w-10 h-10 bg-opacity-50 text-white hover:bg-opacity-70 transition-all duration-200"
            onClick={onClose}
            aria-label="Close modal"
            type="button"
          >
            <Close />
          </button>
        </div>

        {embedCode ? (
          <div
            className="w-full min-h-80"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: sanitizedEmbedCode }}
          />
        ) : (
          <div className="w-full pt-[56.25%] bg-black relative">
            <div className="absolute inset-0 flex items-center justify-center text-white">
              No embed code provided
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmbedModal;
